import { ClientAttributes, Experiment, SettingsConfigurations } from '@pulse/common';
import {
    Observable,
    catchError,
    concatMap,
    distinctUntilChanged,
    firstValueFrom,
    of,
    skip,
    startWith,
    take,
    timeout,
} from 'rxjs';
import { FirstCall } from '../../models';
import { ExperimentsService } from '../experiments.service';
import { SegmentsResolverService } from '../segments-resolver.service';
import { SettingsObserver } from './settings-observer.helper';

export class PulseSettings {
    private firstCallState: FirstCall = {
        firstCallDone: false,
        defaultReturned: false,
    };

    constructor(
        private settingsObserver: SettingsObserver,
        private config: SettingsConfigurations,
        private experimentsService: ExperimentsService,
        private segmentsResolver: SegmentsResolverService,
    ) {}

    observe<T>(
        tag: string,
        settingName: string,
        defaultValue?: any,
        attributes?: ClientAttributes,
        remoteSettingEnabled = true,
    ): Observable<T> {
        return new Observable<T>((observer) => {
            const source$ = this.settingsObserver
                .observe(tag, settingName, defaultValue, remoteSettingEnabled, attributes)
                .pipe(distinctUntilChanged());

            // First emission with timeout
            const first$ = source$.pipe(
                take(1),
                timeout(this.config.timeout),
                catchError(() => of(defaultValue)),
            );

            // Rest of emissions without timeout
            const rest$ = source$.pipe(skip(1));

            const merged$ = first$.pipe(
                concatMap((firstValue) => rest$.pipe(startWith(firstValue))),
            );

            const subscription = merged$.subscribe(observer);

            return () => {
                subscription.unsubscribe();
            };
        });
    }

    async get<T = any>(
        tag: string,
        settingName: string,
        defaults?: any,
        attributes?: ClientAttributes,
        remoteSettingEnabled = true,
    ): Promise<T> {
        return firstValueFrom(
            this.observe(tag, settingName, defaults, attributes, remoteSettingEnabled),
        );
    }

    /**
     * Get the list of user segment id's.
     *
     * Segment identifiers are internal part of the platform and
     * it's strongly recommended to configure and use settings
     * values instead of directly using segment identifiers.
     *
     */
    async segments(attributes: ClientAttributes = {}): Promise<string[]> {
        let segments = await this.segmentsResolver.getSegments();

        if ((!segments || segments?.length < 1) && !this.firstCallState.firstCallDone) {
            await this.get(
                'dummy_tag',
                `${this.config.storagePrefix}_dummy_tag`,
                {},
                attributes,
                true,
            );
            segments = await this.segmentsResolver.getSegments();
        }

        return Promise.resolve(this.segmentsResolver.getSegments());
    }

    async experiments(attributes: ClientAttributes = {}): Promise<Experiment[]> {
        let experiments = this.experimentsService.getExperiments();

        if ((!experiments || experiments?.length < 1) && !this.firstCallState.firstCallDone) {
            await this.get(
                'dummy_tag',
                `${this.config.storagePrefix}_dummy_tag`,
                {},
                attributes,
                true,
            );
            experiments = this.experimentsService.getExperiments();
        }

        return Promise.resolve(experiments);
    }
}
